import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  PlusIcon,
  ServerStackIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { Project, Environment, EnvironmentCreate, EnvironmentUpdate, EnvironmentStatus } from '../types/api';
import Logger from '../utils/logger';
import { getApiErrorMessage } from '../constants/messages';
import { Button, Badge } from '../components/ui';
import { useTranslation } from 'react-i18next';

interface EnvironmentForm {
  nom: string;
  type: 'dev' | 'staging' | 'preproduction' | 'prod';
}

const EnvironmentsPage: React.FC = () => {
  const { t } = (useTranslation as any)('projects');
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [environments, setEnvironments] = useState<Environment[]>([]);
  const [environmentStatuses, setEnvironmentStatuses] = useState<Record<string, EnvironmentStatus>>({});
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingEnvironment, setEditingEnvironment] = useState<Environment | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { register, handleSubmit, reset, formState: { errors } } = useForm<EnvironmentForm>();
  const { register: registerEdit, handleSubmit: handleSubmitEdit, reset: resetEdit, setValue: setValueEdit, formState: { errors: errorsEdit } } = useForm<EnvironmentForm>();

  useEffect(() => {
    if (projectId) {
      loadProjectAndEnvironments();
    }
  }, [projectId]);

  const loadProjectAndEnvironments = async () => {
    if (!projectId) return;

    try {
      setLoading(true);

      // Charger projet et environnements en parallèle
      const [projectData, environmentsData] = await Promise.all([
        apiService.getProject(projectId),
        apiService.getProjectEnvironments(projectId)
      ]);

      setProject(projectData);
      setEnvironments(environmentsData);

      // Charger les status des environnements
      const statusPromises = environmentsData.map(env =>
        apiService.getEnvironmentStatus(env.id)
          .then(status => ({ envId: env.id, status }))
          .catch(error => {
            Logger.warn('Environment status not available');
            return { envId: env.id, status: null };
          })
      );

      const statuses = await Promise.all(statusPromises);
      const statusMap: Record<string, EnvironmentStatus> = {};
      statuses.forEach(({ envId, status }) => {
        if (status) statusMap[envId] = status;
      });

      setEnvironmentStatuses(statusMap);
    } catch (error: unknown) {
      Logger.error('Failed to load project and environments');
      toast.error(t('environments.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: EnvironmentForm) => {
    if (!projectId) return;

    setCreating(true);
    try {
      const environmentData: EnvironmentCreate = {
        nom: data.nom,
        type: data.type
      };

      await apiService.createEnvironment(projectId, environmentData);
      toast.success(t('environments.createSuccess'));

      // Recharger la liste
      await loadProjectAndEnvironments();

      // Reset et fermer le formulaire
      reset();
      setShowCreateForm(false);
    } catch (error: unknown) {
      Logger.error('Failed to create environment');
      const errorMessage = getApiErrorMessage(error) || t('environments.createError');
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleEditEnvironment = (environment: Environment) => {
    setEditingEnvironment(environment);
    setValueEdit('nom', environment.nom);
    setValueEdit('type', environment.type);
  };

  const onSubmitEdit = async (data: EnvironmentForm) => {
    if (!editingEnvironment) return;

    setCreating(true);
    try {
      const environmentData: EnvironmentUpdate = {
        nom: data.nom,
        type: data.type
      };

      await apiService.updateEnvironment(editingEnvironment.id, environmentData);
      toast.success(t('environments.updateSuccess'));

      // Recharger la liste
      await loadProjectAndEnvironments();

      // Reset et fermer le formulaire
      resetEdit();
      setEditingEnvironment(null);
    } catch (error: unknown) {
      Logger.error('Failed to update environment');
      const errorMessage = getApiErrorMessage(error) || t('environments.updateError');
      toast.error(errorMessage);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteEnvironment = async (environment: Environment) => {
    if (!window.confirm(t('environments.confirmDelete', { name: environment.nom }))) {
      return;
    }

    setDeleting(environment.id);
    try {
      await apiService.deleteEnvironment(environment.id);
      toast.success(t('environments.deleteSuccess'));

      // Recharger la liste
      await loadProjectAndEnvironments();
    } catch (error: unknown) {
      Logger.error('Failed to delete environment');
      const errorMessage = getApiErrorMessage(error) || t('environments.deleteError');
      toast.error(errorMessage);
    } finally {
      setDeleting(null);
    }
  };

  const getStatusIcon = (status?: EnvironmentStatus) => {
    if (!status) {
      return <ClockIcon className="h-5 w-5 text-text-quaternary" />;
    }

    if (status.status === 'active' && status.gitlab_config === 'created') {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else if (status.status === 'failed') {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    } else {
      return <ClockIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = (status?: EnvironmentStatus) => {
    if (!status) return t('environments.statusUnknown');
    return `${status.status} - ${status.gitlab_config}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'dev':
        return t('environments.types.dev');
      case 'staging':
        return t('environments.types.staging');
      case 'preproduction':
        return t('environments.types.preproduction');
      case 'prod':
        return t('environments.types.prod');
      default:
        return type;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'dev':
        return 'bg-accent-500/12 text-accent-200';
      case 'staging':
        return 'bg-yellow-500/12 text-yellow-200';
      case 'preproduction':
        return 'bg-orange-500/12 text-orange-200';
      case 'prod':
        return 'bg-red-500/12 text-red-200';
      default:
        return 'bg-surface-3 text-text-primary';
    }
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-500 mx-auto"></div>
        <p className="mt-4 text-text-tertiary">{t('environments.loading')}</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500">{t('environments.notFound')}</p>
        <Link to="/projects" className="text-accent-400 hover:text-accent-300">
          {`← ${t('environments.backToProjects')}`}
        </Link>
      </div>
    );
  }

  return (
    <>
      {/* Breadcrumb */}
      <nav className="flex mb-8" aria-label="Breadcrumb">
        <ol className="flex items-center space-x-4">
          <li>
            <Link to="/projects" className="text-text-quaternary hover:text-text-tertiary">
              <ArrowLeftIcon className="flex-shrink-0 h-5 w-5" />
              <span className="ml-2">{t('environments.breadcrumbProjects')}</span>
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <span className="text-text-quaternary">/</span>
              <span className="ml-4 text-text-tertiary">{project.nom}</span>
            </div>
          </li>
        </ol>
      </nav>

      {/* Header */}
      <div className="lg:flex lg:items-center lg:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-text-primary sm:text-3xl sm:truncate">
            {t('environments.title')}
          </h2>
          <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:mt-0 sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              <ServerStackIcon className="flex-shrink-0 mr-1.5 h-5 w-5 text-text-quaternary" />
              {t('environments.envCount', { count: environments.length })}
            </div>
            <div className="mt-2 flex items-center text-sm text-text-tertiary">
              {t('environments.project')}: <strong className="ml-1">{project.nom}</strong>
            </div>
          </div>
        </div>
        <div className="mt-5 flex lg:mt-0 lg:ml-4">
          <Button
            variant="primary"
            size="md"
            onClick={() => setShowCreateForm(!showCreateForm)}
          >
            <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
            {t('environments.newEnvironment')}
          </Button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <div className="mt-8 bg-surface-2 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary">
              {t('environments.createEnvironment')}
            </h3>
            <div className="mt-5">
              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                <div>
                  <label htmlFor="nom" className="block text-sm font-medium text-text-secondary">
                    {t('environments.envName')}
                  </label>
                  <input
                    {...register('nom', {
                      required: t('environments.nameRequired'),
                      minLength: { value: 1, message: t('environments.nameMinLength') },
                      maxLength: { value: 100, message: t('environments.nameMaxLength') }
                    })}
                    type="text"
                    className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                    placeholder="production, staging, development..."
                  />
                  {errors.nom && (
                    <p className="mt-1 text-sm text-red-600">{errors.nom.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-text-secondary">
                    {t('environments.envType')}
                  </label>
                  <select
                    {...register('type', {
                      required: t('environments.typeRequired')
                    })}
                    className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                  >
                    <option value="dev">{t('environments.types.dev')}</option>
                    <option value="staging">{t('environments.types.staging')}</option>
                    <option value="preproduction">{t('environments.types.preproduction')}</option>
                    <option value="prod">{t('environments.types.prod')}</option>
                  </select>
                  {errors.type && (
                    <p className="mt-1 text-sm text-red-600">{errors.type.message}</p>
                  )}
                </div>

                <p className="text-sm text-text-tertiary" dangerouslySetInnerHTML={{ __html: t('environments.createdInProject', { name: project.nom }) }} />

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => setShowCreateForm(false)}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={creating}
                  >
                    {creating ? t('environments.creating') : t('environments.createButton')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {editingEnvironment && (
        <div className="mt-8 bg-surface-2 shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-text-primary">
              {t('environments.editEnvironment', { name: editingEnvironment.nom })}
            </h3>
            <div className="mt-5">
              <form onSubmit={handleSubmitEdit(onSubmitEdit)} className="space-y-6">
                <div>
                  <label htmlFor="nom-edit" className="block text-sm font-medium text-text-secondary">
                    {t('environments.envName')}
                  </label>
                  <input
                    {...registerEdit('nom', {
                      required: t('environments.nameRequired'),
                      minLength: { value: 1, message: t('environments.nameMinLength') },
                      maxLength: { value: 100, message: t('environments.nameMaxLength') }
                    })}
                    type="text"
                    id="nom-edit"
                    className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                    placeholder="production, staging, development..."
                  />
                  {errorsEdit.nom && (
                    <p className="mt-1 text-sm text-red-600">{errorsEdit.nom.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="type-edit" className="block text-sm font-medium text-text-secondary">
                    {t('environments.envType')}
                  </label>
                  <select
                    {...registerEdit('type', {
                      required: t('environments.typeRequired')
                    })}
                    id="type-edit"
                    className="mt-1 block w-full bg-surface-3 border border-border-1 text-text-primary placeholder-text-quaternary rounded-input shadow-sm focus:ring-accent-500 focus:border-accent-500 sm:text-sm"
                  >
                    <option value="dev">{t('environments.types.dev')}</option>
                    <option value="staging">{t('environments.types.staging')}</option>
                    <option value="preproduction">{t('environments.types.preproduction')}</option>
                    <option value="prod">{t('environments.types.prod')}</option>
                  </select>
                  {errorsEdit.type && (
                    <p className="mt-1 text-sm text-red-600">{errorsEdit.type.message}</p>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    size="md"
                    onClick={() => {
                      setEditingEnvironment(null);
                      resetEdit();
                    }}
                  >
                    {t('cancel', { ns: 'common' })}
                  </Button>
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={creating}
                  >
                    {creating ? t('environments.modifying') : t('environments.modify')}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Environments List */}
      <div className="mt-8">
        {environments.length === 0 ? (
          <div className="text-center py-12">
            <ServerStackIcon className="mx-auto h-12 w-12 text-text-quaternary" />
            <h3 className="mt-2 text-sm font-medium text-text-primary">{t('environments.noEnvironments')}</h3>
            <p className="mt-1 text-sm text-text-tertiary">
              {t('environments.noEnvironmentsHint')}
            </p>
            <div className="mt-6">
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowCreateForm(true)}
              >
                <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
                {t('environments.newEnvironment')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {environments.map((environment) => {
              const status = environmentStatuses[environment.id];
              return (
                <div
                  key={environment.id}
                  className="bg-surface-2 overflow-hidden shadow-card rounded-card hover:shadow-md transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        <ServerStackIcon className="h-6 w-6 text-text-quaternary" />
                      </div>
                      <div className="ml-5 w-0 flex-1">
                        <dl>
                          <dt className="text-sm font-medium text-text-tertiary truncate">
                            {t('environments.environment')}
                          </dt>
                          <dd className="flex items-center space-x-2">
                            <span className="text-lg font-medium text-text-primary">
                              {environment.nom}
                            </span>
                            <Badge
                              variant={
                                environment.type === 'prod'
                                  ? 'error'
                                  : environment.type === 'staging' || environment.type === 'preproduction'
                                  ? 'info'
                                  : 'success'
                              }
                              size="sm"
                            >
                              {getTypeLabel(environment.type)}
                            </Badge>
                          </dd>
                        </dl>
                      </div>
                    </div>

                    {/* Status */}
                    <div className="mt-4 flex items-center">
                      {getStatusIcon(status)}
                      <span className="ml-2 text-sm text-text-secondary">
                        {getStatusText(status)}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="mt-4 space-y-2">
                      <div className="text-xs text-text-tertiary">
                        <span className="font-medium">{t('environments.createdAt')}:</span> {formatDate(environment.date_creation)}
                      </div>
                    </div>
                  </div>

                  <div className="bg-surface-3 px-5 py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex justify-between items-center">
                        <Link
                          to={`/deployments/${environment.id}`}
                          className="inline-flex items-center px-4 py-2 bg-accent-600 text-white text-sm font-medium rounded-lg hover:bg-accent-700 transition-colors duration-200 shadow-sm hover:shadow-md"
                        >
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>
                          </svg>
                          {t('environments.manageDeployments')}
                        </Link>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditEnvironment(environment)}
                          className="inline-flex items-center p-1 border border-transparent rounded text-sm font-medium text-text-tertiary hover:text-accent-500 hover:bg-accent-500/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                          title={t('environments.editTooltip')}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEnvironment(environment)}
                          disabled={deleting === environment.id}
                          className="inline-flex items-center p-1 border border-transparent rounded text-sm font-medium text-text-tertiary hover:text-red-500 hover:bg-red-500/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title={t('environments.deleteTooltip')}
                        >
                          {deleting === environment.id ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500"></div>
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
};

export default EnvironmentsPage;
